/**
 * Geometria das Áreas de Planejamento no front: o polígono vem como na API
 * (shape [{lt,lg}] e/ou geojson Feature/Polygon [lng,lat]).
 *
 * Mesma regra da API (api/utils/areasGeometria.js) para a situação da unidade:
 * 'dentro' | 'fora' | 'sem_centro' pelo centro da cerca-centro.
 */

export const SITUACAO = { DENTRO: "dentro", FORA: "fora", SEM_CENTRO: "sem_centro" };

function parseJson(valor) {
  if (valor == null || valor === "") return null;
  if (typeof valor !== "string") return valor;
  try {
    return JSON.parse(valor);
  } catch (e) {
    return null;
  }
}

function shapeDoGeojson(geojson) {
  let g = parseJson(geojson);
  if (!g) return null;
  if (g.type === "FeatureCollection" && Array.isArray(g.features) && g.features.length) g = g.features[0];
  if (g.type === "Feature") g = g.geometry;
  if (!g || g.type !== "Polygon" || !Array.isArray(g.coordinates) || !Array.isArray(g.coordinates[0])) return null;
  return g.coordinates[0].map((c) => ({ lt: Number(c[1]), lg: Number(c[0]) }));
}

/** Vértices [{lt,lg}] sem o ponto que fecha o anel; null com menos de 3. */
export function verticesDaArea(area) {
  if (!area) return null;
  let pontos = parseJson(area.shape);
  if (!Array.isArray(pontos) || pontos.length < 3) pontos = shapeDoGeojson(area.geojson);
  if (!Array.isArray(pontos)) return null;
  const lista = pontos
    .map((p) => ({
      lt: Number(p && (p.lt != null ? p.lt : p.lat)),
      lg: Number(p && (p.lg != null ? p.lg : p.lng)),
    }))
    .filter((p) => Number.isFinite(p.lt) && Number.isFinite(p.lg));
  if (lista.length > 1) {
    const a = lista[0];
    const b = lista[lista.length - 1];
    if (a.lt === b.lt && a.lg === b.lg) lista.pop();
  }
  return lista.length >= 3 ? lista : null;
}

/** Feature/Polygon com o anel fechado (formato de cercas.geojson). */
export function geojsonDosVertices(vertices) {
  if (!Array.isArray(vertices) || vertices.length < 3) return null;
  const anel = vertices.map((p) => [p.lg, p.lt]);
  anel.push([vertices[0].lg, vertices[0].lt]);
  return { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [anel] } };
}

export function geojsonDaArea(area) {
  return geojsonDosVertices(verticesDaArea(area));
}

/** shape (anel fechado) e geojson em string, como a API grava. */
export function geometriaParaSalvar(vertices) {
  if (!Array.isArray(vertices) || vertices.length < 3) return { shape: null, geojson: null };
  const shape = vertices.concat([vertices[0]]).map((p) => ({ lt: p.lt, lg: p.lg }));
  return { shape: JSON.stringify(shape), geojson: JSON.stringify(geojsonDosVertices(vertices)) };
}

/** Bounding box [minLat, minLng, maxLat, maxLng] dos vértices. */
export function bboxDosVertices(vertices) {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  vertices.forEach((p) => {
    if (p.lt < minLat) minLat = p.lt;
    if (p.lt > maxLat) maxLat = p.lt;
    if (p.lg < minLng) minLng = p.lg;
    if (p.lg > maxLng) maxLng = p.lg;
  });
  return [minLat, minLng, maxLat, maxLng];
}

/** Ray casting (mesma regra do geolib.isPointInPolygon usado na API). */
export function pontoNosVertices(lat, lng, vertices) {
  const y = Number(lat);
  const x = Number(lng);
  if (!Number.isFinite(y) || !Number.isFinite(x) || !Array.isArray(vertices) || vertices.length < 3) return false;
  let dentro = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const yi = vertices[i].lt;
    const xi = vertices[i].lg;
    const yj = vertices[j].lt;
    const xj = vertices[j].lg;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dentro = !dentro;
  }
  return dentro;
}

export function pontoNaArea(lat, lng, area) {
  return pontoNosVertices(lat, lng, verticesDaArea(area));
}

function temCentro(u) {
  return (
    u &&
    u.lat_centro != null &&
    u.lat_centro !== "" &&
    u.lng_centro != null &&
    u.lng_centro !== "" &&
    Number.isFinite(Number(u.lat_centro)) &&
    Number.isFinite(Number(u.lng_centro))
  );
}

export function situacaoDaUnidade(area, unidade) {
  if (!temCentro(unidade)) return SITUACAO.SEM_CENTRO;
  return pontoNaArea(unidade.lat_centro, unidade.lng_centro, area) ? SITUACAO.DENTRO : SITUACAO.FORA;
}

export const MARGEM_AREA_PADRAO_M = 500;
const RAIO_TERRA_M = 6371008.8;
const PASSOS_CIRCULO = 16;

/** Fecho convexo (monotone chain) de pontos [x, y], em sentido anti-horário. */
function fechoConvexo(pontos) {
  const p = pontos.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cruz = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const inferior = [];
  p.forEach((pt) => {
    while (inferior.length >= 2 && cruz(inferior[inferior.length - 2], inferior[inferior.length - 1], pt) <= 0) inferior.pop();
    inferior.push(pt);
  });
  const superior = [];
  for (let i = p.length - 1; i >= 0; i -= 1) {
    const pt = p[i];
    while (superior.length >= 2 && cruz(superior[superior.length - 2], superior[superior.length - 1], pt) <= 0) superior.pop();
    superior.push(pt);
  }
  inferior.pop();
  superior.pop();
  return inferior.concat(superior);
}

/** Anéis externos do geojson da cerca (Polygon, MultiPolygon, Feature, FeatureCollection). */
function aneisDoGeojson(geojson) {
  const g = parseJson(geojson);
  if (!g) return [];
  if (g.type === "FeatureCollection") return (g.features || []).flatMap((f) => aneisDoGeojson(f));
  if (g.type === "Feature") return aneisDoGeojson(g.geometry);
  if (g.type === "Polygon" && Array.isArray(g.coordinates)) return [g.coordinates[0]].filter(Array.isArray);
  if (g.type === "MultiPolygon" && Array.isArray(g.coordinates)) {
    return g.coordinates.map((poly) => poly && poly[0]).filter(Array.isArray);
  }
  return [];
}

/**
 * Geometria da cerca-centro da unidade (campos cerca_* de getunidadessugeridasarea com
 * incluir_cerca), na mesma precedência de api/utils/cercaGeofence.js: círculo quando há raio
 * e centro; senão o polígono (shape, ou o geojson na falta dele).
 * Devolve { raio } | { vertices } | null.
 */
export function geometriaDaCerca(unidade) {
  if (!unidade) return null;
  const raio = Number(unidade.cerca_raio_mts);
  const circular = Number(unidade.cerca_is_circular) === 1 || (Number.isFinite(raio) && raio > 0);
  if (circular && temCentro(unidade)) return { raio: Number.isFinite(raio) && raio > 0 ? raio : 0 };
  const shape = parseJson(unidade.cerca_shape);
  let vertices = Array.isArray(shape) && shape.length >= 3 ? shape : null;
  if (!vertices) {
    vertices = aneisDoGeojson(unidade.cerca_geojson)
      .flat()
      .map((c) => (Array.isArray(c) ? { lt: c[1], lg: c[0] } : null));
  }
  const lista = (vertices || [])
    .map((p) => ({
      lt: Number(p && (p.lt != null ? p.lt : p.lat)),
      lg: Number(p && (p.lg != null ? p.lg : p.lng)),
    }))
    .filter((p) => Number.isFinite(p.lt) && Number.isFinite(p.lg) && Math.abs(p.lt) <= 90 && Math.abs(p.lg) <= 180);
  return lista.length >= 3 ? { vertices: lista } : null;
}

/**
 * Área sugerida a partir das unidades (sentido inverso ao desenho): fecho convexo das cercas-
 * centro com uma margem em metros além delas.
 *   - cerca circular: círculo de raio (raio da cerca + margem) no centro;
 *   - cerca poligonal: cada vértice da cerca vira um círculo de raio = margem;
 *   - sem geometria de cerca (só o centro): círculo de raio = margem no centro.
 * O centro sempre entra com a margem, então todo centro fica dentro e longe da borda (a regra
 * de associação). Os círculos são polígonos de 16 lados circunscritos e a projeção é local
 * (equiretangular na latitude média), suficiente para a escala de uma área de planejamento.
 *
 * Devolve { vertices, usadas, semCentro }; vertices é null sem nenhuma unidade posicionada.
 */
export function areaEnvolvendoUnidades(unidades, margemMetros = MARGEM_AREA_PADRAO_M) {
  const usadas = [];
  const semCentro = [];
  const itens = [];
  (unidades || []).forEach((u) => {
    const cerca = geometriaDaCerca(u);
    const centro = temCentro(u) ? { lt: Number(u.lat_centro), lg: Number(u.lng_centro) } : null;
    if (!centro && !(cerca && cerca.vertices)) {
      semCentro.push(u);
      return;
    }
    if (!centro) semCentro.push(u);
    else usadas.push(u);
    itens.push({ centro, cerca });
  });
  if (!itens.length) return { vertices: null, usadas, semCentro };

  const margem = Math.max(Number(margemMetros) || 0, 1);
  const refs = itens.flatMap((it) => (it.centro ? [it.centro] : it.cerca.vertices));
  const lat0 = refs.reduce((acc, p) => acc + p.lt, 0) / refs.length;
  const lng0 = refs.reduce((acc, p) => acc + p.lg, 0) / refs.length;
  const kx = (Math.PI / 180) * RAIO_TERRA_M * Math.cos((lat0 * Math.PI) / 180);
  const ky = (Math.PI / 180) * RAIO_TERRA_M;
  // Raio do polígono circunscrito: o lado do 16-ágono não entra no círculo.
  const fator = 1 / Math.cos(Math.PI / PASSOS_CIRCULO);

  const pontos = [];
  const circulo = (p, raio) => {
    const x = (p.lg - lng0) * kx;
    const y = (p.lt - lat0) * ky;
    const r = raio * fator;
    for (let i = 0; i < PASSOS_CIRCULO; i += 1) {
      const ang = (2 * Math.PI * i) / PASSOS_CIRCULO;
      pontos.push([x + r * Math.cos(ang), y + r * Math.sin(ang)]);
    }
  };
  itens.forEach(({ centro, cerca }) => {
    if (centro) circulo(centro, margem + (cerca && cerca.raio ? cerca.raio : 0));
    if (cerca && cerca.vertices) cerca.vertices.forEach((v) => circulo(v, margem));
  });
  const vertices = fechoConvexo(pontos).map(([x, y]) => ({
    lt: Number((lat0 + y / ky).toFixed(7)),
    lg: Number((lng0 + x / kx).toFixed(7)),
  }));
  return { vertices: vertices.length >= 3 ? vertices : null, usadas, semCentro };
}

/**
 * Zonas preparadas para testes repetidos (bbox + vértices): use com zonasDoPonto,
 * rotaCruzaZonas e paradasEmZonas.
 */
export function prepararZonas(areas) {
  return (areas || [])
    .map((a) => {
      const vertices = verticesDaArea(a);
      if (!vertices) return null;
      return { area: a, vertices, bbox: bboxDosVertices(vertices) };
    })
    .filter(Boolean);
}

function noBbox(lat, lng, bbox) {
  return lat >= bbox[0] && lat <= bbox[2] && lng >= bbox[1] && lng <= bbox[3];
}

/** Zonas (preparadas) que contêm o ponto. */
export function zonasDoPonto(lat, lng, zonasPreparadas) {
  const y = Number(lat);
  const x = Number(lng);
  if (!Number.isFinite(y) || !Number.isFinite(x)) return [];
  return (zonasPreparadas || [])
    .filter((z) => noBbox(y, x, z.bbox) && pontoNosVertices(y, x, z.vertices))
    .map((z) => z.area);
}

function orientacao(ax, ay, bx, by, cx, cy) {
  const v = (by - ay) * (cx - bx) - (bx - ax) * (cy - by);
  if (Math.abs(v) < 1e-15) return 0;
  return v > 0 ? 1 : 2;
}

function noSegmento(ax, ay, bx, by, cx, cy) {
  return bx <= Math.max(ax, cx) && bx >= Math.min(ax, cx) && by <= Math.max(ay, cy) && by >= Math.min(ay, cy);
}

function segmentosCruzam(p1, p2, p3, p4) {
  const o1 = orientacao(p1[0], p1[1], p2[0], p2[1], p3[0], p3[1]);
  const o2 = orientacao(p1[0], p1[1], p2[0], p2[1], p4[0], p4[1]);
  const o3 = orientacao(p3[0], p3[1], p4[0], p4[1], p1[0], p1[1]);
  const o4 = orientacao(p3[0], p3[1], p4[0], p4[1], p2[0], p2[1]);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && noSegmento(p1[0], p1[1], p3[0], p3[1], p2[0], p2[1])) return true;
  if (o2 === 0 && noSegmento(p1[0], p1[1], p4[0], p4[1], p2[0], p2[1])) return true;
  if (o3 === 0 && noSegmento(p3[0], p3[1], p1[0], p1[1], p4[0], p4[1])) return true;
  if (o4 === 0 && noSegmento(p3[0], p3[1], p2[0], p2[1], p4[0], p4[1])) return true;
  return false;
}

/** Normaliza pontos de uma polyline: [lat,lng], {lat,lng} ou {lt,lg}. */
function normalizarPontos(latlngs) {
  return (latlngs || [])
    .map((p) => {
      if (Array.isArray(p)) return [Number(p[0]), Number(p[1])];
      if (p && p.lat != null) return [Number(p.lat), Number(p.lng)];
      if (p && p.lt != null) return [Number(p.lt), Number(p.lg)];
      return null;
    })
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
}

/**
 * Zonas (preparadas) que a polyline toca: algum vértice dentro ou algum segmento cruzando
 * uma aresta da zona. Corta pela bbox da rota antes do teste fino.
 */
export function rotaCruzaZonas(latlngs, zonasPreparadas) {
  const pts = normalizarPontos(latlngs);
  if (!pts.length || !(zonasPreparadas || []).length) return [];
  const bboxRota = bboxDosVertices(pts.map((p) => ({ lt: p[0], lg: p[1] })));
  const tocadas = [];
  zonasPreparadas.forEach((z) => {
    const b = z.bbox;
    if (bboxRota[0] > b[2] || bboxRota[2] < b[0] || bboxRota[1] > b[3] || bboxRota[3] < b[1]) return;
    if (pts.some((p) => noBbox(p[0], p[1], b) && pontoNosVertices(p[0], p[1], z.vertices))) {
      tocadas.push(z.area);
      return;
    }
    const anel = z.vertices.map((v) => [v.lt, v.lg]);
    for (let i = 1; i < pts.length; i += 1) {
      const a = pts[i - 1];
      const c = pts[i];
      if (Math.max(a[0], c[0]) < b[0] || Math.min(a[0], c[0]) > b[2] || Math.max(a[1], c[1]) < b[1] || Math.min(a[1], c[1]) > b[3]) continue;
      for (let k = 0, j = anel.length - 1; k < anel.length; j = k++) {
        if (segmentosCruzam(a, c, anel[j], anel[k])) {
          tocadas.push(z.area);
          return;
        }
      }
    }
  });
  return tocadas;
}

/** [{ parada, zonas }] das paradas ({lat,lng} ou [lat,lng]) que caem em zona. */
export function paradasEmZonas(paradas, zonasPreparadas) {
  const out = [];
  (paradas || []).forEach((parada) => {
    const p = normalizarPontos([parada])[0];
    if (!p) return;
    const zonas = zonasDoPonto(p[0], p[1], zonasPreparadas);
    if (zonas.length) out.push({ parada, zonas });
  });
  return out;
}

export function rotuloArea(area) {
  if (!area) return "";
  return [area.cod_area, area.nome_area].filter(Boolean).join(" - ");
}
